/**
 * Promotions Service
 * Handles all promotion-related API calls
 */

import { apiService, ApiResponse } from './api.service';

/**
 * Promotion Type Enum
 */
export type PromotionType = 'PERCENTAGE' | 'FIXED_AMOUNT' | 'BUY_X_GET_Y' | 'FREE_SHIPPING';

/**
 * Promotion Status Enum
 */
export type PromotionStatus = 'DRAFT' | 'ACTIVE' | 'SCHEDULED' | 'EXPIRED' | 'DISABLED';

/**
 * Promotion Interface
 */
export interface Promotion {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: PromotionType;
  discountValue: number;
  minPurchase?: number;
  maxDiscount?: number;
  startDate: string;
  endDate: string;
  usageLimit?: number;
  usageCount: number;
  isActive: boolean;
  status: PromotionStatus;
  tenantId: string;
  branchId?: string;
  branchName?: string;
  applicableProducts?: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Create Promotion DTO
 */
export interface CreatePromotionDto {
  code: string;
  name: string;
  description?: string;
  type: PromotionType;
  status?: PromotionStatus;
  discountValue: number;
  minPurchase?: number;
  maxDiscount?: number;
  startDate: string;
  endDate: string;
  usageLimit?: number;
  perUserLimit?: number;
  isActive?: boolean;
}

/**
 * Update Promotion DTO
 */
export interface UpdatePromotionDto {
  code?: string;
  name?: string;
  description?: string;
  type?: PromotionType;
  status?: PromotionStatus;
  discountValue?: number;
  minPurchase?: number;
  maxDiscount?: number;
  startDate?: string;
  endDate?: string;
  usageLimit?: number;
  perUserLimit?: number;
  isActive?: boolean;
}

/**
 * Promotions Query Parameters
 */
export interface PromotionsQuery {
  page?: number;
  limit?: number;
  status?: PromotionStatus;
  type?: PromotionType;
  search?: string;
}

/**
 * Paginated Promotions Response
 */
export interface PaginatedPromotions {
  data: Promotion[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Validate Promotion Response
 */
export interface ValidatePromotionResponse {
  valid: boolean;
  promotion?: Promotion;
  discount: number;
  message?: string;
}

/**
 * Promotions Service
 */
class PromotionsService {
  /**
   * Get all promotions with pagination and filters
   */
  async getPromotions(query?: PromotionsQuery): Promise<ApiResponse<PaginatedPromotions>> {
    try {
      const params = new URLSearchParams();
      
      if (query?.page) params.append('page', query.page.toString());
      if (query?.limit) params.append('limit', query.limit.toString());
      if (query?.status) params.append('status', query.status);
      if (query?.type) params.append('type', query.type);
      if (query?.search) params.append('search', query.search);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<PaginatedPromotions>(`/promotions${queryString}`);
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
      throw error;
    }
  }

  /**
   * Get single promotion by ID
   */
  async getPromotionById(id: string): Promise<ApiResponse<Promotion>> {
    try {
      return await apiService.get<Promotion>(`/promotions/${id}`);
    } catch (error) {
      console.error('Failed to fetch promotion:', error);
      throw error;
    }
  }

  /**
   * Create new promotion
   */
  async createPromotion(data: CreatePromotionDto): Promise<ApiResponse<Promotion>> {
    try {
      return await apiService.post<Promotion>('/promotions', data);
    } catch (error) {
      console.error('Failed to create promotion:', error);
      throw error;
    }
  }

  /**
   * Update promotion
   */
  async updatePromotion(id: string, data: UpdatePromotionDto): Promise<ApiResponse<Promotion>> {
    try {
      return await apiService.patch<Promotion>(`/promotions/${id}`, data);
    } catch (error) {
      console.error('Failed to update promotion:', error);
      throw error;
    }
  }

  /**
   * Delete promotion
   */
  async deletePromotion(id: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(`/promotions/${id}`);
    } catch (error) {
      console.error('Failed to delete promotion:', error);
      throw error;
    }
  }

  /**
   * Validate promotion code
   */
  async validatePromotion(code: string, orderTotal: number): Promise<ApiResponse<ValidatePromotionResponse>> {
    try {
      const response = await apiService.get<Promotion>(`/promotions/code/${encodeURIComponent(code)}`);

      if (!response.success || !response.data) {
        return { success: true, data: { valid: false, discount: 0, message: 'Invalid promotion code' } };
      }

      const promotion = response.data;
      const discount = promotion.type === 'PERCENTAGE'
        ? (orderTotal * Number(promotion.discountValue)) / 100
        : Number(promotion.discountValue);

      return {
        success: true,
        data: {
          valid: true,
          promotion,
          discount,
        },
      };
    } catch (error) {
      console.error('Failed to validate promotion:', error);
      throw error;
    }
  }

  /**
   * Toggle promotion active status
   */
  async togglePromotionStatus(id: string): Promise<ApiResponse<Promotion>> {
    try {
      const current = await this.getPromotionById(id);
      if (!current.success || !current.data) {
        throw new Error('Promotion not found');
      }

      return await apiService.patch<Promotion>(`/promotions/${id}`, {
        isActive: !current.data.isActive,
        status: current.data.isActive ? 'DISABLED' : 'ACTIVE',
      });
    } catch (error) {
      console.error('Failed to toggle promotion status:', error);
      throw error;
    }
  }

  /**
   * Get promotion statistics
   */
  async getPromotionStats(): Promise<ApiResponse<{
    total: number;
    active: number;
    expired: number;
    totalUsage: number;
    totalDiscount: number;
  }>> {
    try {
      return await apiService.get('/promotions/stats');
    } catch (error) {
      console.error('Failed to fetch promotion stats:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const promotionsService = new PromotionsService();
